import { useMemo, useEffect } from 'react';
import useApi from '../../hooks/api/useApi';
import useMutate from '../../hooks/api/useMutate';

export function useBusinessTypes() {
  const { data, isLoading } = useApi('/business-types', {
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });
  const types = useMemo(() => data?.data ?? [], [data]);
  return { types, isLoading };
}

let hasFetchedLogos = false;

// GET logos
// export function useRestaurantLogos() {
//   const { data, error, isLoading, refresh } = useApi('/restaurant/logos', {
//     revalidateOnMount: !hasFetchedLogos,
//     revalidateOnFocus: false,
//     revalidateOnReconnect: false,
//     revalidateIfStale: false,
//     keepPreviousData: true
//   });

//   useEffect(() => {
//     if (data && !hasFetchedLogos) {
//       hasFetchedLogos = true;
//     }
//   }, [data]);

//   const logos = useMemo(() => {
//     return {
//       light: data?.data?.logos?.light || '',
//       dark: data?.data?.logos?.dark || '',
//       favicon: data?.data?.logos?.favicon || ''
//     };
//   }, [data]);

//   return {
//     logos,
//     error,
//     isLoading,
//     refresh
//   };
// }

// The restaurant's logos and name, kept in this browser after a sign-in so
// the login page (where nobody is signed in yet, and the address does not
// say which restaurant) can show the same branding next time.
const BRANDING_KEY = 'yz-branding';

/** The branding saved by the last sign-in on this browser, or null. */
export function readSavedBranding() {
  try {
    const saved = JSON.parse(localStorage.getItem(BRANDING_KEY) || 'null');
    return saved && typeof saved === 'object' ? saved : null;
  } catch {
    return null;
  }
}

function saveBranding(branding) {
  try {
    localStorage.setItem(BRANDING_KEY, JSON.stringify(branding));
  } catch {
    // Storage blocked or full: the login page just keeps the default.
  }
}

/** Points the page's icon links (the tab's favicon) at `href`. */
function setTabIcon(href) {
  document.querySelectorAll("link[rel~='icon']").forEach((l) => {
    l.setAttribute('href', href);
    // The default links declare PNG; an uploaded icon may be another type.
    l.removeAttribute('type');
  });
}

export function useRestaurantLogos(userId) {
  const { data, error, isLoading, refresh } = useApi(userId ? `/restaurant/logos?user=${userId}` : null, {
    revalidateOnMount: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    keepPreviousData: false
  });

  const logos = useMemo(
    () => ({
      light: data?.data?.logos?.light || '',
      dark: data?.data?.logos?.dark || '',
      favicon: data?.data?.logos?.favicon || ''
    }),
    [data]
  );

  // Every time the logos load, so a change in Settings reaches the login
  // page from the next visit on.
  useEffect(() => {
    if (!data?.data) return;
    saveBranding({ ...logos, name: data.data.restaurant_name || '' });
  }, [data, logos]);

  // The browser tab wears the favicon set in Settings → General, across the
  // admin app — and a new one as soon as it is saved (Settings refreshes these
  // logos, and every user of this hook shares them). Without one set, the
  // app's own icon stays.
  useEffect(() => {
    if (logos.favicon) setTabIcon(logos.favicon);
  }, [logos.favicon]);

  return { logos, error, isLoading, refresh };
}

//update logo
export function useUpdateRestaurantLogo() {
  const { post, isMutating } = useMutate('/restaurant/upload-logo');

  const updateLogo = async (formData) => {
    return await post(formData);
  };

  return {
    updateLogo,
    isMutating
  };
}

//update general settings
export function useUpdateRestaurantSettings() {
  const { patch, isMutating } = useMutate('/restaurant/update');

  const updateSettings = async (payload) => {
    return await patch(payload);
  };

  return {
    updateSettings,
    isMutating
  };
}

export function useUpdateRestaurantTheme() {
  const { post, isMutating } = useMutate('/restaurant/theme/update');

  const updateTheme = async (payload) => {
    return await post(payload);
  };

  return {
    updateTheme,
    isMutating
  };
}
